import { useState } from "react";
import { IRecords, IStrikeData, IContractData } from "../../../types";
import OptionChainTable from "../../StrategyBuilder/table";
import DropdownButton from "react-bootstrap/DropdownButton";
import { Dropdown } from "react-bootstrap";
import { getAtmCallStrikeIndex } from "../../../calculations/strategyUtils";
import OIChart from "./oi-chart";
import GlassyButton from "../../glassy-button";

interface IOptionChainProps {
  data: IRecords;
  expiry: string;
  selectedExpiry: (exp: string) => void;
}

const OptionChain = ({ data, expiry, selectedExpiry }: IOptionChainProps) => {
  const { expiryDates, underlyingValue } = data;
  const filteredData = data.data.filter((val) => val.expiryDates === expiry);
  const strikes = filteredData.map((val) => val.strikePrice);
  const atmCallIndex = getAtmCallStrikeIndex(strikes, underlyingValue);
  const startIndex = Math.max(0, atmCallIndex - 12);
  const [showChart, setShowChart] = useState(true);

  const dataSource = filteredData.slice(startIndex, startIndex + 24);
  let totalChangeInCallOI = 0;
  let totalChangeInPutOI = 0;
  dataSource.forEach((val) => {
    if (val.CE) totalChangeInCallOI += val.CE.changeinOpenInterest;
    if (val.PE) totalChangeInPutOI += val.PE.changeinOpenInterest;
  });
  let totalCallOI = 0;
  let totalPutOI = 0;
  for (let i = 0; i < filteredData.length; i++) {
    if (filteredData[i].CE) totalCallOI += filteredData[i].CE!.openInterest;
    if (filteredData[i].PE) totalPutOI += filteredData[i].PE!.openInterest;
  }
  const getITMClassName = (strike: number, isCall: boolean): string => {
    if (isCall) {
      return strike < underlyingValue ? "atm-row-oc " : "";
    }
    return strike > underlyingValue ? "atm-row-oc " : "";
  };

  const renderOptionChainCard = (
    value: IContractData | undefined,
    isCall: boolean
  ) => {
    if (value === undefined) return null;
    return (
      <div className={"flexbox " + getITMClassName(value.strikePrice, isCall)}>
        <div>Price: {value.lastPrice + ", " + Math.round(value.pChange)}%</div>

        <div>Volume: {value.totalTradedVolume}</div>
        <div>
          OI:{" "}
          {value.openInterest + ", " + Math.round(value.pchangeinOpenInterest)}%
        </div>
        <div>IV: {value.impliedVolatility}</div>
      </div>
    );
  };
  const fields = [
    {
      text: "Calls",
      render: (value: IStrikeData) => renderOptionChainCard(value.CE, true),
    },
    {
      text: "Strike",
      field: "strikePrice",
      render: (value: any) => {
        return (
          <span style={{ fontWeight: "bold" }} id={value.strikePrice}>
            {value.strikePrice}
          </span>
        );
      },
    },
    {
      text: "Puts",
      render: (value: IStrikeData) => renderOptionChainCard(value.PE, false),
    },
  ];
  const putCallRatio = totalPutOI / totalCallOI;
  return (
    <div>
      <div style={{ textAlign: "center" }}>
        <DropdownButton
          id="dropdown-basic-button"
          title={"Expiry: " + expiry}
          size="sm"
          variant="success"
        >
          {expiryDates.map((val, index) => {
            return (
              <Dropdown.Item
                key={index}
                onSelect={() => {
                  selectedExpiry(val);
                }}
              >
                {val}
              </Dropdown.Item>
            );
          })}
        </DropdownButton>
      </div>
      <div
        className={
          (putCallRatio >= 1 ? "text-success" : "text-danger") +
          " text-md-left fw-bold"
        }
        style={{ textAlign: "center" }}
      >
        Put Call Ratio: {putCallRatio.toPrecision(3)} indicates{" "}
        {putCallRatio >= 1.1
          ? "Bullishness"
          : putCallRatio >= 0.9
          ? "Neutral"
          : "Bearishness"}
      </div>
      <div style={{ textAlign: "center" }}>
        <b>Calls OI Change: </b>
        {totalChangeInCallOI}
      </div>
      <div style={{ textAlign: "center" }}>
        <b>Puts OI Change:</b> {totalChangeInPutOI}
      </div>
      {totalChangeInCallOI > totalChangeInPutOI ? (
        <div
          className="text-danger text-md-left fw-bold"
          style={{ textAlign: "center" }}
        >
          OI change indicates Bearish sentiment
        </div>
      ) : (
        <div
          className="text-success text-md-left fw-bold"
          style={{ textAlign: "center" }}
        >
          OI change indicates Bullish sentiment
        </div>
      )}
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "row",
        gap: "8px",
        paddingBottom: "8px",
      }}>
        {["Chart", "Table"].map((value) => (
          <GlassyButton
            key={value}
            text={value}
            onClick={() => setShowChart(value === "Chart")}
            isActive={showChart ? value === "Chart" : value === "Table"}
          />
        ))}
      </div>
      {showChart ? (
        <OIChart data={data} selectedExpiry={expiry} />
      ) : (
        <OptionChainTable
          dataSource={dataSource}
          showRemoveIcon={false}
          fields={fields}
          bordered={false}
        />
      )}
    </div>
  );
};

export default OptionChain;